import ModalGenerateObjective, {
  GenerateObjectiveFunction,
} from '@/components/astro.ai/generate/ModalGenerateObjective';
import MainButton from '@/components/ui/custom/buttons/MainButton';
import ConditionalWrapper from '@/components/ui/custom/ConditionalWrapper';
import { Tooltip } from '@/components/ui/custom/dataDisplay/Tooltip';
import { useAbortController } from '@/data/hook/useAbortController';
import useSafePortal from '@/data/hook/useSafePortal';
import useToggle from '@/data/hook/useToggle';
import { objectivesGenerator } from '@/data/services/astro.ai/generateServices';
import { TempObjective } from '@/models/CoursePath';
import { SparklesIcon } from '@heroicons/react/24/outline';
import { useTranslation } from 'react-i18next';
import { Fragment } from 'react/jsx-runtime';
import { GenerateActionProps } from './GenerateActionProps';

export const GenerateObjectivesAction = ({
  setValue,
  courseId,
  disabled,
  locked,
  hasValue,
}: GenerateActionProps) => {
  const createSafePortal = useSafePortal();
  const { t } = useTranslation('translation', {
    keyPrefix: 'courseStudio.actions',
  });
  const { signal, abort } = useAbortController();

  const generate: GenerateObjectiveFunction = async args => {
    if (!courseId) return;
    const { objectives } = await objectivesGenerator(
      {
        courseId,
        ...args,
      },
      signal,
    );

    const tempObjectives = objectives.map(
      obj =>
        new TempObjective({
          name: obj.name,
          description: obj.description,
        }),
    );
    setValue('objectives', tempObjectives);
  };

  const onCancel = () => {
    abort();
    close();
  };

  const { isOpen, close, open } = useToggle();

  return (
    <Fragment>
      <ConditionalWrapper
        condition={!!locked}
        wrapper={<Tooltip text={t('generateObjectives.locked')} />}
      >
        <MainButton
          onClick={open}
          icon={<SparklesIcon className="w-4 h-4" />}
          text={t('generateObjectives.name')}
          disabled={disabled || locked || !courseId}
        />
      </ConditionalWrapper>
      {createSafePortal(
        <ModalGenerateObjective
          visible={isOpen}
          onClose={onCancel}
          onGenerate={generate}
          hasValue={hasValue}
        />,
      )}
    </Fragment>
  );
};
